import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import HelpButton from "../components/HelpButton";
import LeadDetailDrawer from "../components/leads/LeadDetailDrawer";
import HelpFieldIcon from "../components/HelpFieldIcon";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { buildLeadVars } from "../utils/whatsappContext";
import { hasPerm } from "../utils/permissions";
import { toast } from "sonner";
import {
  WhatsappLogo,
  UserCircle,
  Phone,
  EnvelopeSimple,
  PencilSimple,
  ClockClockwise,
  X,
  CheckCircle,
  Warning,
  Plus,
  Handshake,
  Trash,
  ArrowCounterClockwise,
  FileText,
  CalendarBlank,
  ListBullets,
  UserSwitch,
} from "@phosphor-icons/react";
import LeadsFollowUpCalendar from "../components/leads/LeadsFollowUpCalendar";
import LeadTransferModal from "../components/leads/LeadTransferModal";
import BulkTransferModal from "../components/leads/BulkTransferModal";
import TransferApprovalsDrawer from "../components/leads/TransferApprovalsDrawer";
import OpenInTabLink from "../components/common/OpenInTabLink";
import SourceSelect from "../components/leads/SourceSelect";
import FollowupStatusSelect from "../components/leads/FollowupStatusSelect";
import { statusBadgeCls, fetchStatuses } from "../components/leads/FollowupStatusSelect";

// ---------- helpers ----------
const STATUS_OPTIONS = ["new", "contacted", "qualified", "converted", "lost"];
const PRIORITY_OPTIONS = ["hot", "warm", "cold"];
const CUSTOMER_TYPES = [
  "retail", "dealer", "builder", "corporate", "government",
  "installer", "electrician", "amc", "service", "bulk",
];
const LEAD_CATEGORIES = [
  "cctv", "biometric", "home_automation", "networking", "amc", "service", "other",
];
const FOLLOW_UP_TYPES = [
  "call", "whatsapp", "email", "visit", "demo",
  "quotation_followup", "payment_followup", "dealer_followup",
];
const FOLLOW_UP_OUTCOMES = [
  // F1 — practical activity outcomes (matches backend FOLLOW_UP_OUTCOMES_F1 + legacy "scheduled")
  ["scheduled", "Scheduled"],
  ["call_connected", "Call Connected"],
  ["call_not_answered", "Call Not Answered"],
  ["customer_asked_to_call_later", "Asked to Call Later"],
  ["requirement_collected", "Requirement Collected"],
  ["quotation_requested", "Quotation Requested"],
  ["price_negotiation", "Price Negotiation"],
  ["site_visit_scheduled", "Site Visit Scheduled"],
  ["demo_scheduled", "Demo Scheduled"],
  ["customer_not_interested", "Not Interested"],
  ["wrong_number", "Wrong Number"],
  ["lead_converted", "Converted"],
  ["lead_lost", "Lost"],
  ["transferred_to_another_bde", "Transferred"],
];

// Frontend mirror of backend OUTCOME_TO_STATUS — used only to AUTO-SUGGEST a status
// when the user picks an outcome. User can override the suggested value freely.
const OUTCOME_TO_STATUS_HINT = {
  call_connected: "interested",
  call_not_answered: "no_response",
  customer_asked_to_call_later: "follow_up_pending",
  requirement_collected: "interested",
  quotation_requested: "quotation_required",
  price_negotiation: "interested",
  site_visit_scheduled: "site_visit_required",
  demo_scheduled: "demo_required",
  customer_not_interested: "not_interested",
  wrong_number: "lost",
  lead_converted: "converted",
  lead_lost: "lost",
  transferred_to_another_bde: "transferred",
  scheduled: "call_scheduled",
};

const getStatusColor = (status) => {
  switch (status) {
    case "new": return "bg-blue-50 text-blue-700 border-blue-200";
    case "contacted": return "bg-amber-50 text-amber-700 border-amber-200";
    case "qualified": return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "converted": return "bg-green-50 text-green-700 border-green-200";
    case "lost": return "bg-rose-50 text-rose-700 border-rose-200";
    default: return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

const getPriorityColor = (p) => {
  switch (p) {
    case "hot": return "bg-rose-50 text-rose-700 border-rose-200";
    case "warm": return "bg-amber-50 text-amber-700 border-amber-200";
    case "cold": return "bg-sky-50 text-sky-700 border-sky-200";
    default: return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

const emptyLead = {
  name: "", email: "", phone: "", whatsapp: "",
  company_name: "", city: "", state: "",
  customer_type: "", source: "", lead_category: "",
  product_requirement: "", budget: 0, priority: "warm",
  lead_score: 0, status: "new", notes: "",
};

// ---------- main component ----------
const Leads = ({ user, logout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(200);
  const [bdes, setBdes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [newLead, setNewLead] = useState(emptyLead);

  const [editLead, setEditLead] = useState(null);          // lead being edited
  const [followLead, setFollowLead] = useState(null);      // lead whose follow-ups we're viewing
  // Phase F1 — additive
  const [viewMode, setViewMode] = useState("list");        // "list" | "calendar"
  const [transferLead, setTransferLead] = useState(null);  // lead being transferred
  // Batch B (Feb 2026) — Bulk transfer + Approvals drawer
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);
  const [showApprovals, setShowApprovals] = useState(false);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [search, setSearch] = useState("");
  // Phase Audit-1 — backend audit filters (Created By / Assigned By / Source / Follow-up status / Date range)
  const [filterCreatedById, setFilterCreatedById] = useState("");
  const [filterCreatedByRole, setFilterCreatedByRole] = useState("");
  const [filterAssignedToId, setFilterAssignedToId] = useState("");
  const [filterAssignedById, setFilterAssignedById] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterFollowupStatus, setFilterFollowupStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showAdvFilters, setShowAdvFilters] = useState(false);
  const [allUsers, setAllUsers] = useState([]); // populates Created By / Assigned To / Assigned By dropdowns
  // Phase A — Show Deleted Leads (admin only)
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { lead, linked? }
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Phase C — Lead Detail Drawer
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [waModal, setWaModal] = useState(null);
  const [convertLead, setConvertLead] = useState(null);   // Phase 5a — lead being converted to deal
  const [convertForm, setConvertForm] = useState({ title: "", value: 0, stage: "prospect", probability: 20, expected_close: "", notes: "" });
  const [convertBusy, setConvertBusy] = useState(false);

  const canAssign = ["admin", "manager", "bdm"].includes(user?.role) && hasPerm(user, "leads.assign");
  const isAdmin = user?.role === "admin";
  const canDeleteLead = isAdmin && hasPerm(user, "leads.delete");
  const canEditLead = hasPerm(user, "leads.edit_own") || hasPerm(user, "leads.edit_all") || hasPerm(user, "leads.edit");
  // Batch B — Per-lead transfer visibility:
  // - admin/md/manager/bdm: any lead (direct transfer)
  // - bde: only leads they own (creates pending request)
  const _role = (user?.role || "").toLowerCase();
  const canTransferLead = (lead) => {
    if (["admin", "md", "manager", "bdm"].includes(_role)) return true;
    if (_role === "bde") {
      return (lead?.assigned_to_id || "") === (user?.user_id || "")
        || (lead?.followup_owner_id || "") === (user?.user_id || "");
    }
    return false;
  };
  const canBulkTransfer = ["admin", "md", "manager", "bdm"].includes(_role);
  const canViewApprovals = ["admin", "md", "manager", "bdm"].includes(_role);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || "",
        requester_user_id: user?.user_id || "",
      });
      if (showDeleted && isAdmin) params.append("include_deleted", "true");
      // Phase Audit-1 — push audit filters server-side so MongoDB does the heavy lifting
      if (filterCreatedById)     params.append("created_by_id", filterCreatedById);
      if (filterCreatedByRole)   params.append("created_by_role", filterCreatedByRole);
      if (filterAssignedToId)    params.append("assigned_to_id", filterAssignedToId);
      if (filterAssignedById)    params.append("assigned_by_id", filterAssignedById);
      if (filterSource)          params.append("source", filterSource);
      if (filterFollowupStatus)  params.append("followup_status", filterFollowupStatus);
      if (filterDateFrom)        params.append("date_from", filterDateFrom);
      if (filterDateTo)          params.append("date_to", filterDateTo);
      if (filterStatus !== "all")   params.append("status", filterStatus);
      if (filterPriority !== "all") params.append("temperature", filterPriority);
      // Server-side pagination + free-text search
      params.append("with_total", "true");
      params.append("limit", String(pageSize));
      params.append("skip", String(page * pageSize));
      if (search && search.trim()) params.append("q", search.trim());
      // Manager/Admin/BDM see filter dropdowns populated from /api/users
      const canSeeAllUsers = ["admin", "manager", "bdm", "marketing_head", "md", "digital_marketing"].includes(user?.role);
      const [leadsRes, usersRes] = await Promise.all([
        api.get(`${API}/leads?${params.toString()}`),
        canSeeAllUsers ? api.get(`/users`) : Promise.resolve({ data: [] }),
      ]);
      // New envelope: {leads, total, limit, skip}. Fallback if older response shape arrives.
      const envelope = leadsRes.data;
      if (envelope && Array.isArray(envelope.leads)) {
        setLeads(envelope.leads);
        setTotalLeads(envelope.total ?? envelope.leads.length);
      } else {
        setLeads(envelope || []);
        setTotalLeads((envelope || []).length);
      }
      const users = usersRes.data || [];
      setBdes(users.filter(u => u.role === "bde" || u.role === "bdm"));
      setAllUsers(users.filter(u => u.role && u.role !== "customer"));
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.name, user?.user_id, showDeleted, isAdmin, page, pageSize, search,
      filterCreatedById, filterCreatedByRole, filterAssignedToId, filterAssignedById,
      filterSource, filterFollowupStatus, filterDateFrom, filterDateTo,
      filterStatus, filterPriority]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Reset to page 0 whenever a filter/search changes so the user always lands on
  // the first page of the new result set (avoids "Page 7 of 2" empty-page UX).
  useEffect(() => {
    setPage(0);
  }, [search, showDeleted, filterCreatedById, filterCreatedByRole, filterAssignedToId,
      filterAssignedById, filterSource, filterFollowupStatus, filterDateFrom, filterDateTo,
      filterStatus, filterPriority]);

  // Popup "Open" handoff — when the BDE clicks Open on a popup card, it navigates
  // to /leads?openLead=<lead_id>&tab=followups. Here we read those params, fetch
  // the lead directly from the API (the in-memory `leads` list may be filtered
  // and not include it), then open the Follow-ups drawer.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const openId = sp.get("openLead");
    const tab = sp.get("tab");
    if (!openId) return;
    let cancelled = false;
    (async () => {
      try {
        // Try the in-memory list first (fast path)
        const fromList = leads.find((l) => l.lead_id === openId);
        let target = fromList;
        if (!target) {
          const r = await api.get(`${API}/leads/${openId}`, {
            params: {
              requester_user_id: user?.user_id || "",
              requester_name: user?.name || "",
              requester_role: user?.role || "",
            },
          });
          target = r.data;
        }
        if (cancelled) return;
        if (!target?.lead_id) {
          toast.error("Lead not accessible — you may not have permission to view it.");
        } else if (tab === "followups") {
          setFollowLead(target);
        } else {
          setEditLead(target);
        }
      } catch (e) {
        if (!cancelled) toast.error("Lead not accessible — you may not have permission to view it.");
      } finally {
        if (!cancelled) navigate("/leads", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [location.search, navigate, leads, user]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newLead,
        budget: Number(newLead.budget) || 0,
        lead_score: Number(newLead.lead_score) || 0,
      };
      // URGENT FIX — pass requester info so backend can stamp creator + auto-assign to BDE/BDM
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      });
      await api.post(`${API}/leads?${params.toString()}`, payload);
      toast.success("Lead added successfully!");
      setShowAdd(false);
      setNewLead(emptyLead);
      fetchLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to add lead");
    }
  };

  const handleEditSave = async () => {
    if (!editLead) return;
    try {
      const patch = {
        name: editLead.name,
        email: editLead.email || "",
        phone: editLead.phone || "",
        whatsapp: editLead.whatsapp || "",
        company_name: editLead.company_name || "",
        city: editLead.city || "",
        state: editLead.state || "",
        customer_type: editLead.customer_type || "",
        source: editLead.source || "",
        lead_category: editLead.lead_category || "",
        product_requirement: editLead.product_requirement || "",
        budget: Number(editLead.budget) || 0,
        priority: editLead.priority || "warm",
        lead_score: Number(editLead.lead_score) || 0,
        status: editLead.status || "new",
        notes: editLead.notes || "",
      };
      await api.put(`${API}/leads/${editLead.lead_id}?requester_role=${user?.role || ""}`, patch);
      toast.success("Lead updated");
      setEditLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update lead");
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await api.put(`${API}/leads/${leadId}/status?status=${status}`);
      toast.success("Lead status updated!");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const assignLead = async (leadId, bdeUserId) => {
    if (!bdeUserId) return;
    try {
      const qs = new URLSearchParams({
        bde_user_id: bdeUserId,
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      await api.put(`${API}/leads/${leadId}/assign?${qs.toString()}`);
      toast.success("Lead assigned");
      fetchLeads();
    } catch (err) { toast.error("Assignment failed"); }
  };

  const openWhatsApp = async (lead) => {
    if (!lead.phone && !lead.whatsapp) { toast.error("No phone number on this lead"); return; }
    setWaModal({
      module: "lead",
      receiver: { name: lead.name, phone: lead.whatsapp || lead.phone, type: "customer", id: lead.lead_id },
      defaultAction: "first_contact",
      context: { variables: buildLeadVars(lead) },
    });
  };

  // Phase 5a — convert lead to deal
  const submitConvertLead = async () => {
    if (!convertLead) return;
    if (!convertForm.title.trim()) { toast.error("Deal title is required"); return; }
    setConvertBusy(true);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      });
      const r = await api.post(`${API}/leads/${convertLead.lead_id}/convert-to-deal?${params.toString()}`, convertForm);
      toast.success(r.data?.message === "Lead already converted" ? "Already converted — opening existing deal" : "Lead converted to deal");
      setConvertLead(null);
      fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally {
      setConvertBusy(false);
    }
  };

  // Phase B — Lead → Quotation Direct Flow (Invisible Deal)
  // One-click: server auto-creates Deal if needed, returns pre-filled estimation seed,
  // then we navigate to BillingCenter with the seed as router state — modal opens automatically.
  const [quickQuoteBusy, setQuickQuoteBusy] = useState(null); // lead_id while a request is in flight
  const quickQuote = async (lead) => {
    if (quickQuoteBusy) return;
    setQuickQuoteBusy(lead.lead_id);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      });
      const r = await api.post(`${API}/leads/${lead.lead_id}/quick-quote?${params.toString()}`);
      const seed = r.data?.seed || {};
      navigate("/billing", { state: { quickQuoteSeed: seed } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start quotation");
    } finally {
      setQuickQuoteBusy(null);
    }
  };

  // Phase A — Delete (soft) + Restore lead (Admin only)
  const requestDeleteLead = (lead) => {
    setDeleteConfirm({ lead, linked: null, force: false });
  };

  const performDeleteLead = async (force = false) => {
    if (!deleteConfirm) return;
    const lead = deleteConfirm.lead;
    setDeleteBusy(true);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || "",
        requester_user_id: user?.user_id || "",
      });
      if (force) params.append("force", "true");
      await api.delete(`${API}/leads/${lead.lead_id}?${params.toString()}`);
      toast.success(`Lead "${lead.name}" deleted`);
      setDeleteConfirm(null);
      fetchLeads();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // Linked records — show second confirmation in same modal
        const linked = e?.response?.data?.detail?.linked || null;
        setDeleteConfirm({ lead, linked, force: true });
      } else {
        toast.error(e?.response?.data?.detail || "Delete failed");
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const restoreLead = async (lead) => {
    try {
      const params = new URLSearchParams({ requester_role: user?.role || "" });
      await api.post(`${API}/leads/${lead.lead_id}/restore?${params.toString()}`);
      toast.success(`Lead "${lead.name}" restored`);
      fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  // ----- client-side text search only (status/priority/audit filters all server-side) -----
  const filteredLeads = leads.filter(l => {
    if (search) {
      const q = search.toLowerCase();
      const blob = `${l.name || ""} ${l.email || ""} ${l.phone || ""} ${l.company_name || ""} ${l.product_requirement || ""}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />

      <div className="flex-1 ml-64">
        <div className="page-header-sticky">
          <div className="px-8 pt-5 pb-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                  Leads
                </h1><HelpButton moduleKey="lead" /></div>
                <p className="text-base leading-relaxed text-slate-700">
                  Track customer leads, priority, follow-ups & conversions
                </p>
              </div>
              <div className="flex items-center gap-2">
                {canViewApprovals && (
                  <button
                    data-testid="approvals-open-btn"
                    onClick={() => setShowApprovals(true)}
                    className="rounded-none border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-3 font-medium transition-colors duration-300 inline-flex items-center gap-2"
                    title="Transfer Approvals"
                  >
                    <UserSwitch size={16} weight="bold" /> Approvals
                  </button>
                )}
                {canBulkTransfer && (
                  <button
                    data-testid="bulk-transfer-open-btn"
                    onClick={() => setShowBulkTransfer(true)}
                    className="rounded-none border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-4 py-3 font-medium transition-colors duration-300 inline-flex items-center gap-2"
                    title="Bulk Transfer"
                  >
                    <UserSwitch size={16} weight="bold" /> Bulk Transfer
                  </button>
                )}
                <button
                  data-testid="add-lead-btn"
                  onClick={() => { setShowAdd(!showAdd); setNewLead(emptyLead); }}
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 inline-flex items-center gap-2"
                >
                  {showAdd ? <><X size={16} /> Cancel</> : <><Plus size={16} weight="bold" /> Add Lead</>}
                </button>
              </div>
            </div>

            {/* Phase F1 — List / Calendar toggle */}
            <div className="flex items-center gap-1 mb-3">
              <button data-testid="leads-view-list" onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border inline-flex items-center gap-1 ${viewMode === "list" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                <ListBullets size={12} weight="bold" /> List
              </button>
              <button data-testid="leads-view-calendar" onClick={() => setViewMode("calendar")}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border inline-flex items-center gap-1 ${viewMode === "calendar" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                <CalendarBlank size={12} weight="bold" /> Follow-up Calendar
              </button>
            </div>

            {/* Filters — primary row (always visible) */}
            <div data-testid="leads-filters" className="bg-white border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              <input
                data-testid="leads-search"
                type="text"
                placeholder="Search name, phone, company…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-2 outline-none md:col-span-3"
              />
              {/* Primary BDE filter — mirrors Deals page "All BDEs" UX. Shares filterAssignedToId
                  state with the "Assigned To" dropdown inside More Filters, so both stay in sync.
                  Options come from `allUsers`, which is only populated server-side for roles
                  already authorised to see the full user list (same source as More Filters). */}
              <select
                data-testid="filter-bde"
                value={filterAssignedToId}
                onChange={(e) => setFilterAssignedToId(e.target.value)}
                className="rounded-none bg-white border border-slate-300 p-2 outline-none md:col-span-2"
                title="Filter by BDE"
              >
                <option value="">All BDEs</option>
                {allUsers.filter(u => ["bde", "bdm"].includes(u.role)).map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name} ({(u.role || "").toUpperCase()})</option>
                ))}
              </select>
              <select
                data-testid="filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded-none bg-white border border-slate-300 p-2 outline-none md:col-span-2"
              >
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                data-testid="filter-priority"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="rounded-none bg-white border border-slate-300 p-2 outline-none md:col-span-2"
              >
                <option value="all">All priorities</option>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <button
                type="button"
                data-testid="toggle-adv-filters"
                onClick={() => setShowAdvFilters((v) => !v)}
                className={`rounded-none border px-3 py-2 text-xs font-bold uppercase tracking-wider md:col-span-2 ${showAdvFilters ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              >
                {showAdvFilters ? "Hide Filters" : "More Filters"}
              </button>
              {isAdmin && (
                <button
                  data-testid="toggle-deleted-leads"
                  type="button"
                  onClick={() => setShowDeleted((v) => !v)}
                  className={`rounded-none border px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 md:col-span-1 ${
                    showDeleted ? 'bg-red-600 text-white border-red-600 hover:bg-red-700' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <Trash size={14} weight={showDeleted ? 'fill' : 'bold'} />
                  {showDeleted ? 'Trash' : 'Show Deleted'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-8 pt-4 pb-8">
          {/* Filters — advanced (audit / role / source / follow-up / date range) */}
          {showAdvFilters && (
            <div data-testid="leads-adv-filters" className="bg-slate-50 border border-slate-200 p-4 mb-3 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Created By</label>
                <select
                  data-testid="filter-created-by"
                  value={filterCreatedById}
                  onChange={(e) => setFilterCreatedById(e.target.value)}
                  className="rounded-none w-full bg-white border border-slate-300 p-2 text-sm outline-none"
                >
                  <option value="">Anyone</option>
                  {allUsers.map(u => <option key={u.user_id} value={u.user_id}>{`${u.name} · ${u.role.toUpperCase()}`}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Assigned To</label>
                <select
                  data-testid="filter-assigned-to"
                  value={filterAssignedToId}
                  onChange={(e) => setFilterAssignedToId(e.target.value)}
                  className="rounded-none w-full bg-white border border-slate-300 p-2 text-sm outline-none"
                >
                  <option value="">Anyone</option>
                  {allUsers.filter(u => ["bde","bdm","manager"].includes(u.role)).map(u => (
                    <option key={u.user_id} value={u.user_id}>{`${u.name} · ${u.role.toUpperCase()}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Assigned By</label>
                <select
                  data-testid="filter-assigned-by"
                  value={filterAssignedById}
                  onChange={(e) => setFilterAssignedById(e.target.value)}
                  className="rounded-none w-full bg-white border border-slate-300 p-2 text-sm outline-none"
                >
                  <option value="">Anyone</option>
                  {allUsers.map(u => <option key={u.user_id} value={u.user_id}>{`${u.name} · ${u.role.toUpperCase()}`}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Created By Role</label>
                <select
                  data-testid="filter-created-by-role"
                  value={filterCreatedByRole}
                  onChange={(e) => setFilterCreatedByRole(e.target.value)}
                  className="rounded-none w-full bg-white border border-slate-300 p-2 text-sm outline-none"
                >
                  <option value="">All roles</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="bdm">BDM</option>
                  <option value="bde">BDE</option>
                  <option value="digital_marketing">Digital Marketing</option>
                  <option value="marketing_head">Marketing Head</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Lead Source</label>
                <SourceSelect
                  testId="filter-source"
                  mode="filter"
                  value={filterSource}
                  onChange={setFilterSource}
                  user={user}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Follow-up Status</label>
                <FollowupStatusSelect
                  value={filterFollowupStatus}
                  onChange={setFilterFollowupStatus}
                  user={user}
                  mode="filter"
                  testId="filter-followup-status"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Created From</label>
                <input
                  data-testid="filter-date-from"
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="rounded-none w-full bg-white border border-slate-300 p-2 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Created To</label>
                <input
                  data-testid="filter-date-to"
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="rounded-none w-full bg-white border border-slate-300 p-2 text-sm outline-none"
                />
              </div>
              <div className="md:col-span-4 flex justify-end">
                <button
                  type="button"
                  data-testid="filter-clear"
                  onClick={() => {
                    setFilterCreatedById(""); setFilterCreatedByRole("");
                    setFilterAssignedToId(""); setFilterAssignedById("");
                    setFilterSource(""); setFilterFollowupStatus("");
                    setFilterDateFrom(""); setFilterDateTo("");
                  }}
                  className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{page * pageSize + 1}</span>–<span className="font-semibold text-slate-900">{Math.min((page + 1) * pageSize, totalLeads)}</span> of <span className="font-semibold text-slate-900">{totalLeads.toLocaleString()}</span>
              {showDeleted && <span className="ml-2 text-red-600 font-semibold">(Trash)</span>}
              {filteredLeads.length !== leads.length && (
                <span className="ml-2 text-slate-500">· {filteredLeads.length} on this page after view filters</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="leads-page-prev" disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="px-3 py-1 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                ← Prev
              </button>
              <span className="px-3 py-1 text-xs text-slate-600 tabular-nums">
                Page {page + 1} of {Math.max(1, Math.ceil(totalLeads / pageSize))}
              </span>
              <button data-testid="leads-page-next" disabled={(page + 1) * pageSize >= totalLeads}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                Next →
              </button>
            </div>
          </div>

          {showAdd && (
            <LeadForm
              data={newLead}
              setData={setNewLead}
              onSubmit={handleAdd}
              onCancel={() => { setShowAdd(false); setNewLead(emptyLead); }}
              testIdPrefix="lead"
              title="Add New Lead"
              submitLabel="Add Lead"
              user={user}
            />
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : viewMode === "calendar" ? (
            <LeadsFollowUpCalendar user={user} onEventClick={(ev) => {
              const target = leads.find(l => l.lead_id === ev.lead_id);
              if (target) setFollowLead(target);
            }} />
          ) : (
            <div data-testid="leads-table" className="bg-white border border-slate-200 rounded-none shadow-sm relative">
              <table className="w-full min-w-[1100px] table-fixed">
                <thead className="sticky top-0 z-30 bg-white shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]">
                  <tr className="bg-slate-50 border-y border-slate-200">
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[180px]">Name / Company</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[170px]">Contact</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[90px]">Category</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[70px]">Priority</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[90px]">Budget</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[130px]">Created By</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[130px]">Lead Owner</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[130px]">Assigned By</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[130px]">Follow-up</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 w-[80px]">Status</th>
                    <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 sticky right-0 z-20 w-[130px] shadow-[-4px_0_6px_-3px_rgba(0,0,0,0.08)]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead, index) => {
                    const nextDue = lead.next_follow_up_at;
                    const isOverdue = nextDue && new Date(nextDue) < new Date();
                    return (
                      <tr key={lead.lead_id} data-testid={`lead-row-${index}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-300 align-top">
                        <td className="p-4">
                          <div className="flex items-start">
                            <a
                              href={`/leads/${lead.lead_id}`}
                              onClick={(e) => {
                                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                e.preventDefault();
                                setDrawerLeadId(lead.lead_id);
                              }}
                              data-testid={`lead-open-drawer-${index}`}
                              className="text-left font-semibold text-slate-900 hover:text-[#002FA7] hover:underline transition-colors block"
                              title="Click to open lead detail · Ctrl/Cmd-Click to open in new tab"
                            >
                              {lead.name}
                            </a>
                            <OpenInTabLink to={`/leads/${lead.lead_id}`} testId={`lead-open-tab-${index}`} />
                          </div>
                          {lead.company_name && <div className="text-xs text-slate-500 mt-0.5">{lead.company_name}</div>}
                          {(lead.city || lead.state) && (
                            <div className="text-xs text-slate-400 mt-0.5">{[lead.city, lead.state].filter(Boolean).join(", ")}</div>
                          )}
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {lead.phone && <div className="flex items-center gap-1"><Phone size={12} /> {lead.phone}</div>}
                          {lead.email && <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5"><EnvelopeSimple size={12} /> {lead.email}</div>}
                          {lead.source && <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">via {lead.source}</div>}
                        </td>
                        <td className="p-4 text-sm">
                          {lead.lead_category && (
                            <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 text-xs uppercase tracking-wider">{lead.lead_category.replace(/_/g, " ")}</span>
                          )}
                          {lead.customer_type && (
                            <div className="text-xs text-slate-500 mt-1 capitalize">{lead.customer_type}</div>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getPriorityColor(lead.priority || "warm")}`}>
                            {lead.priority || "warm"}
                          </span>
                          {!!lead.lead_score && (
                            <div className="text-[10px] text-slate-500 mt-1">Score {lead.lead_score}</div>
                          )}
                        </td>
                        <td className="p-4 text-sm text-slate-700">
                          {lead.budget ? `₹${Number(lead.budget).toLocaleString("en-IN")}` : "—"}
                        </td>
                        {/* Created By — name + role + source (legacy-safe) */}
                        <td className="p-3 text-xs leading-snug" data-testid={`lead-created-by-${index}`}>
                          <div className="font-semibold text-slate-900">{lead.created_by_name || "Unknown"}</div>
                          <div className="text-slate-500 uppercase tracking-wider text-[10px]">
                            {(lead.created_by_role || "—").toUpperCase()}
                            {lead.created_source ? <> · {lead.created_source.replace(/_/g, " ")}</> : null}
                          </div>
                          {lead.created_at && (
                            <div className="text-slate-400 text-[10px] mt-0.5">{formatDate(lead.created_at)}</div>
                          )}
                        </td>
                        {/* Assigned To — name + role */}
                        <td className="p-3 text-xs leading-snug" data-testid={`lead-assigned-to-${index}`}>
                          {lead.assigned_to ? (
                            <>
                              <div className="font-semibold text-slate-900 inline-flex items-center gap-1">
                                <UserCircle size={12} weight="fill" className="text-[#002FA7]" />
                                {lead.assigned_to}
                              </div>
                              <div className="text-slate-500 uppercase tracking-wider text-[10px]">
                                {(lead.assigned_to_role || "—").toUpperCase()}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-400 text-[10px] uppercase tracking-wider">Unassigned</span>
                          )}
                        </td>
                        {/* Assigned By — name + role + date (System / Auto-Assignment for legacy) */}
                        <td className="p-3 text-xs leading-snug" data-testid={`lead-assigned-by-${index}`}>
                          {lead.assigned_to ? (
                            <>
                              <div className="font-semibold text-slate-900">{lead.assigned_by_name || "Unknown"}</div>
                              <div className="text-slate-500 uppercase tracking-wider text-[10px]">
                                {(lead.assigned_by_role || "—").toUpperCase().replace(/_/g, " ")}
                              </div>
                              {lead.assigned_at && (
                                <div className="text-slate-400 text-[10px] mt-0.5">{formatDate(lead.assigned_at)}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-400 text-[10px] uppercase tracking-wider">—</span>
                          )}
                        </td>
                        {/* Follow-up — last + next + owner */}
                        <td className="p-3 text-xs leading-snug" data-testid={`lead-followup-cell-${index}`}>
                          {nextDue ? (
                            <div className={`inline-flex items-center gap-1 ${isOverdue ? "text-rose-700 font-semibold" : "text-slate-700"}`}>
                              {isOverdue ? <Warning size={12} weight="fill" /> : <ClockClockwise size={12} />}
                              <span className="text-[11px]">Next: {formatDateTime(nextDue)}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-[10px] uppercase tracking-wider">No follow-up</span>
                          )}
                          {lead.last_follow_up_at && (
                            <div className="text-slate-500 text-[10px] mt-0.5">Last: {formatDate(lead.last_follow_up_at)}</div>
                          )}
                          {lead.followup_owner_name && (
                            <div className="text-slate-500 text-[10px] mt-0.5">Owner: {lead.followup_owner_name}</div>
                          )}
                          {!!lead.follow_up_count && (
                            <div className="text-[10px] text-slate-400 mt-0.5">{lead.follow_up_count} logged</div>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusColor(lead.status)}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="p-4 sticky right-0 z-10 bg-white shadow-[-4px_0_6px_-3px_rgba(0,0,0,0.08)]" data-testid={`lead-actions-cell-${index}`}>
                          <div className="flex items-center gap-1 flex-wrap">
                            <select
                              data-testid={`lead-status-select-${index}`}
                              value={lead.status}
                              onChange={(e) => updateStatus(lead.lead_id, e.target.value)}
                              className="rounded-none bg-white border border-slate-300 text-xs p-1.5 outline-none"
                            >
                              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {canAssign && (
                              <select
                                data-testid={`lead-assign-${index}`}
                                value={lead.assigned_to_id || ""}
                                onChange={(e) => assignLead(lead.lead_id, e.target.value)}
                                className="rounded-none bg-white border border-slate-300 text-xs p-1.5 outline-none max-w-[110px]"
                                title="Assign"
                              >
                                <option value="">Assign…</option>
                                {bdes.map(b => (
                                  <option key={b.user_id} value={b.user_id}>{b.name}</option>
                                ))}
                              </select>
                            )}
                            <button
                              data-testid={`lead-followups-${index}`}
                              onClick={() => setFollowLead(lead)}
                              className="p-1.5 text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors"
                              title="Follow-ups"
                            >
                              <ClockClockwise size={16} weight="bold" />
                            </button>
                            {canTransferLead(lead) && (
                              <button
                                data-testid={`lead-transfer-${index}`}
                                onClick={() => setTransferLead(lead)}
                                className="p-1.5 text-purple-600 hover:bg-purple-50 transition-colors"
                                title={_role === "bde" ? "Request transfer" : "Transfer to another BDE"}
                              >
                                <UserSwitch size={16} weight="bold" />
                              </button>
                            )}
                            <button
                              data-testid={`lead-edit-${index}`}
                              onClick={() => setEditLead({ ...lead })}
                              className={`p-1.5 text-slate-700 hover:bg-slate-200 transition-colors ${canEditLead ? "" : "hidden"}`}
                              title="Edit"
                            >
                              <PencilSimple size={16} weight="bold" />
                            </button>
                            {(lead.phone || lead.whatsapp) && (
                              <button
                                data-testid={`lead-whatsapp-${index}`}
                                onClick={() => openWhatsApp(lead)}
                                className="p-1.5 text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
                                title="WhatsApp"
                              >
                                <WhatsappLogo size={16} weight="fill" />
                              </button>
                            )}
                            {/* Phase 5a — Convert to Deal / View linked Deal */}
                            {lead.linked_deal_id ? (
                              <a
                                data-testid={`lead-view-deal-${index}`}
                                href="/crm/deals"
                                className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1 ml-1"
                                title={`Linked deal: ${lead.linked_deal_title || ''}`}
                              >
                                <Handshake size={12} weight="bold" /> View Deal
                              </a>
                            ) : (
                              <button
                                data-testid={`lead-convert-deal-${index}`}
                                onClick={() => {
                                  setConvertLead(lead);
                                  setConvertForm({
                                    title: `${lead.name || "Lead"} — ${lead.product_requirement || "Deal"}`,
                                    value: Number(lead.budget || 0),
                                    stage: "prospect",
                                    probability: lead.priority === "hot" ? 60 : lead.priority === "warm" ? 30 : 15,
                                    expected_close: "",
                                    notes: lead.notes || "",
                                  });
                                }}
                                className="text-[10px] uppercase tracking-wider px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 font-bold inline-flex items-center gap-1 ml-1"
                                title="Convert to Deal"
                              >
                                <Handshake size={12} weight="bold" /> Deal
                              </button>
                            )}
                            {/* Phase B — Lead → Quotation Direct Flow (Invisible Deal) */}
                            <button
                              data-testid={`lead-quick-quote-${index}`}
                              onClick={() => quickQuote(lead)}
                              disabled={quickQuoteBusy === lead.lead_id}
                              className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1 ml-1 disabled:opacity-60"
                              title="Create Quotation"
                            >
                              <FileText size={12} weight="bold" />
                              {quickQuoteBusy === lead.lead_id ? "Loading…" : "Quote"}
                            </button>
                            {/* Phase A — Admin-only: Delete (soft) or Restore */}
                            {canDeleteLead && !lead.is_deleted && (
                              <button
                                data-testid={`lead-delete-${index}`}
                                onClick={() => requestDeleteLead(lead)}
                                className="p-1.5 text-red-600 hover:bg-red-50 transition-colors"
                                title="Delete Lead"
                              >
                                <Trash size={16} weight="bold" />
                              </button>
                            )}
                            {isAdmin && lead.is_deleted && (
                              <button
                                data-testid={`lead-restore-${index}`}
                                onClick={() => restoreLead(lead)}
                                className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1 ml-1"
                                title="Restore Lead"
                              >
                                <ArrowCounterClockwise size={12} weight="bold" /> Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredLeads.length === 0 && (
                    <tr><td colSpan={11} className="p-12 text-center text-slate-400">No leads match the filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Phase C — Lead Detail Drawer */}
      {drawerLeadId && (() => {
        const liveLead = leads.find((l) => l.lead_id === drawerLeadId);
        if (!liveLead) { return null; }
        return (
          <LeadDetailDrawer
            lead={liveLead}
            user={user}
            onClose={() => setDrawerLeadId(null)}
            onLeadChange={() => fetchLeads()}
            onQuickQuote={(l) => { setDrawerLeadId(null); quickQuote(l); }}
          />
        );
      })()}

      {/* Edit modal */}
      {editLead && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
          <div data-testid="edit-lead-modal" className="bg-white border border-slate-200 w-full max-w-4xl my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>Edit Lead — {editLead.name}</h3>
              <button data-testid="edit-lead-close" onClick={() => setEditLead(null)} className="p-1 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="p-6">
              <LeadForm
                data={editLead}
                setData={setEditLead}
                onSubmit={(e) => { e.preventDefault(); handleEditSave(); }}
                onCancel={() => setEditLead(null)}
                testIdPrefix="edit-lead"
                title=""
                submitLabel="Save Changes"
                showStatusAndScore
                user={user}
              />
            </div>
          </div>
        </div>
      )}

      {/* Phase A — Delete-confirm modal (with linked-doc warning + force confirm) */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" data-testid="delete-lead-modal">
          <div className="bg-white border border-slate-200 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Warning size={18} weight="fill" className="text-red-600" />
                {deleteConfirm.linked ? 'Lead Has Linked Records' : 'Delete Lead?'}
              </h3>
              <button data-testid="delete-lead-close" onClick={() => setDeleteConfirm(null)} className="p-1 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm text-slate-700">
              <div>
                Lead: <span className="font-semibold text-slate-900">{deleteConfirm.lead.name}</span>
                {deleteConfirm.lead.company_name && <span className="text-slate-500"> · {deleteConfirm.lead.company_name}</span>}
              </div>
              {!deleteConfirm.linked ? (
                <p>This will remove the lead from the active list. It will not be removed from the database (soft-delete) — admin can restore it later from <span className="font-semibold">Show Deleted</span>.</p>
              ) : (
                <>
                  <p>
                    This lead has linked records:
                  </p>
                  <ul className="bg-amber-50 border border-amber-200 p-3 text-xs space-y-1 list-disc list-inside" data-testid="delete-lead-linked-summary">
                    {deleteConfirm.linked.has_deal && (
                      <li>Linked deal: <span className="font-semibold">{deleteConfirm.linked.deal_title || deleteConfirm.linked.deal_id}</span></li>
                    )}
                    {deleteConfirm.linked.quotation_count > 0 && (
                      <li>{deleteConfirm.linked.quotation_count} linked quotation(s)</li>
                    )}
                    {deleteConfirm.linked.sales_order_count > 0 && (
                      <li>{deleteConfirm.linked.sales_order_count} linked sales order(s)</li>
                    )}
                  </ul>
                  <p className="text-slate-600">
                    Linked records <span className="font-semibold text-slate-900">will not be touched</span>. Only the lead is soft-deleted from the active list.
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
              <button
                data-testid="delete-lead-cancel"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteBusy}
                className="rounded-none border border-slate-300 bg-white text-slate-700 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                data-testid={deleteConfirm.linked ? 'delete-lead-force-confirm' : 'delete-lead-confirm'}
                onClick={() => performDeleteLead(Boolean(deleteConfirm.linked))}
                disabled={deleteBusy}
                className="rounded-none bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                <Trash size={14} weight="bold" />
                {deleteConfirm.linked ? 'Delete Anyway' : 'Delete Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase F1 — Lead Transfer modal */}
      <LeadTransferModal
        open={!!transferLead}
        lead={transferLead}
        user={user}
        onClose={() => setTransferLead(null)}
        onTransferred={() => fetchLeads()}
      />

      {/* Batch B — Bulk Transfer modal + Approvals drawer */}
      <BulkTransferModal
        open={showBulkTransfer}
        user={user}
        onClose={() => setShowBulkTransfer(false)}
        onCompleted={() => fetchLeads()}
      />
      <TransferApprovalsDrawer
        open={showApprovals}
        user={user}
        onClose={() => setShowApprovals(false)}
        onChanged={() => fetchLeads()}
      />

      {/* Follow-ups drawer */}
      {followLead && (
        <FollowUpsDrawer
          lead={followLead}
          user={user}
          onClose={() => { setFollowLead(null); fetchLeads(); }}
        />
      )}

      {/* Phase 5a: Convert Lead to Deal modal */}
      {convertLead && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" data-testid="convert-deal-modal">
          <div className="bg-white border border-slate-200 w-full max-w-lg shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Handshake size={18} className="text-blue-700" weight="bold" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Convert to Deal</div>
                  <h3 className="font-semibold text-slate-900">{convertLead.name}</h3>
                </div>
              </div>
              <button data-testid="convert-deal-close" onClick={() => setConvertLead(null)} className="p-1 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Deal Title *</label>
                <input data-testid="convert-deal-title" value={convertForm.title} onChange={e => setConvertForm({...convertForm, title: e.target.value})} className="w-full rounded-none border border-slate-300 focus:border-blue-600 p-2 text-sm outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Deal Value (₹)</label>
                  <input data-testid="convert-deal-value" type="number" value={convertForm.value} onChange={e => setConvertForm({...convertForm, value: Number(e.target.value)})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none tabular-nums" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Stage</label>
                  <select data-testid="convert-deal-stage" value={convertForm.stage} onChange={e => setConvertForm({...convertForm, stage: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {["prospect", "requirement_collected", "quotation_required"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Probability %</label>
                  <input type="number" min="0" max="100" value={convertForm.probability} onChange={e => setConvertForm({...convertForm, probability: Number(e.target.value)})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none tabular-nums" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Expected Close</label>
                  <input type="date" value={convertForm.expected_close} onChange={e => setConvertForm({...convertForm, expected_close: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Notes</label>
                <textarea value={convertForm.notes} onChange={e => setConvertForm({...convertForm, notes: e.target.value})} rows={3} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                Lead details will auto-link: <strong>{convertLead.name}</strong> · {convertLead.phone || "no phone"} · {convertLead.product_requirement || "no product"}
              </div>
            </div>
            <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setConvertLead(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="convert-deal-submit" disabled={convertBusy} onClick={submitConvertLead} className="rounded-none bg-blue-700 text-white hover:bg-blue-800 px-5 py-2 text-sm font-medium disabled:bg-slate-300 inline-flex items-center gap-2">
                <Handshake size={14} weight="bold" /> {convertBusy ? "Converting…" : "Create Deal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 4b-2: WhatsApp Send modal */}
      {waModal && <WhatsAppSendModal open={true} onClose={() => setWaModal(null)} {...waModal} user={user} />}
    </div>
  );
};

// ---------- Lead form (used for Add and Edit) ----------
const LeadForm = ({ data, setData, onSubmit, onCancel, testIdPrefix, title, submitLabel, showStatusAndScore = false, user = null }) => {
  const set = (k, v) => setData({ ...data, [k]: v });
  const inputCls = "rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none";
  const labelCls = "text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2";

  return (
    <div data-testid={`${testIdPrefix}-form`} className={title ? "bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm" : ""}>
      {title && <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>{title}</h3>}
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>Name *</label>
          <input data-testid={`${testIdPrefix}-name-input`} type="text" value={data.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} required />
        </div>
        <div>
          <label className={labelCls}>Company</label>
          <input data-testid={`${testIdPrefix}-company-input`} type="text" value={data.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input data-testid={`${testIdPrefix}-phone-input`} type="tel" value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>WhatsApp</label>
          <input data-testid={`${testIdPrefix}-whatsapp-input`} type="tel" value={data.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} placeholder="If different from phone" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid={`${testIdPrefix}-email-input`} type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Source <HelpFieldIcon moduleKey="lead" fieldKey="lead_source" /></label>
          <SourceSelect
            testId={`${testIdPrefix}-source-input`}
            value={data.source || ""}
            onChange={(v) => set("source", v)}
            user={user}
            className=""
          />
        </div>
        <div>
          <label className={labelCls}>City</label>
          <input data-testid={`${testIdPrefix}-city-input`} type="text" value={data.city || ""} onChange={(e) => set("city", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>State</label>
          <input data-testid={`${testIdPrefix}-state-input`} type="text" value={data.state || ""} onChange={(e) => set("state", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Customer Type</label>
          <select data-testid={`${testIdPrefix}-customer-type`} value={data.customer_type || ""} onChange={(e) => set("customer_type", e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {CUSTOMER_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Lead Category</label>
          <select data-testid={`${testIdPrefix}-category`} value={data.lead_category || ""} onChange={(e) => set("lead_category", e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Priority</label>
          <select data-testid={`${testIdPrefix}-priority`} value={data.priority || "warm"} onChange={(e) => set("priority", e.target.value)} className={inputCls}>
            {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Budget (₹)</label>
          <input data-testid={`${testIdPrefix}-budget`} type="number" min="0" step="1" value={data.budget || 0} onChange={(e) => set("budget", e.target.value)} className={inputCls} />
        </div>
        {showStatusAndScore && (
          <>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid={`${testIdPrefix}-status`} value={data.status || "new"} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Lead Score (0-100)</label>
              <input data-testid={`${testIdPrefix}-score`} type="number" min="0" max="100" value={data.lead_score || 0} onChange={(e) => set("lead_score", e.target.value)} className={inputCls} />
            </div>
          </>
        )}
        <div className="md:col-span-2">
          <label className={labelCls}>Product Requirement</label>
          <textarea data-testid={`${testIdPrefix}-requirement`} value={data.product_requirement || ""} onChange={(e) => set("product_requirement", e.target.value)} className={inputCls} rows="2" placeholder="e.g., 8 IP cameras with NVR, biometric access for 2 doors" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Notes</label>
          <textarea data-testid={`${testIdPrefix}-notes-input`} value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inputCls} rows="3" />
        </div>
        <div className="md:col-span-2 flex gap-3">
          <button data-testid={`${testIdPrefix}-submit-btn`} type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300">
            {submitLabel}
          </button>
          {onCancel && (
            <button data-testid={`${testIdPrefix}-cancel-btn`} type="button" onClick={onCancel} className="rounded-none bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-3 font-medium transition-colors">
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

// ---------- Follow-ups drawer ----------
const FollowUpsDrawer = ({ lead, user, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusMeta, setStatusMeta] = useState([]);
  useEffect(() => { fetchStatuses().then(setStatusMeta); }, []);
  const [form, setForm] = useState({
    type: "call",
    remarks: "",
    next_follow_up_at: "",
    outcome: "scheduled",
    status: "",
    statusManuallySet: false,
    completed_at: "",
  });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${API}/leads/${lead.lead_id}/follow-ups`);
      setItems(res.data || []);
    } catch (e) {
      toast.error("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  }, [lead.lead_id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.remarks.trim() && form.outcome !== "scheduled") {
      toast.error("Please add remarks");
      return;
    }
    try {
      await api.post(`${API}/leads/${lead.lead_id}/follow-ups`, {
        lead_id: lead.lead_id,
        type: form.type,
        remarks: form.remarks,
        next_follow_up_at: fromLocalInput(form.next_follow_up_at) || null,
        outcome: form.outcome,
        status: form.status || "",   // master Follow-up Status (server auto-fills if blank via OUTCOME_TO_STATUS)
        completed_at: form.outcome === "completed" ? (fromLocalInput(form.completed_at) || new Date().toISOString()) : null,
        by_user_id: user?.user_id || "",
        by_user_name: user?.name || "",
      });
      toast.success("Follow-up logged");
      setShowForm(false);
      setForm({ type: "call", remarks: "", next_follow_up_at: "", outcome: "scheduled", status: "", statusManuallySet: false, completed_at: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to log follow-up");
    }
  };

  const markComplete = async (followUpId) => {
    try {
      await api.put(`${API}/follow-ups/${followUpId}`, {
        outcome: "completed",
        completed_at: new Date().toISOString(),
      });
      toast.success("Marked complete");
      load();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const remove = async (followUpId) => {
    if (!window.confirm("Delete this follow-up?")) return;
    try {
      await api.delete(`${API}/follow-ups/${followUpId}?requester_role=${user?.role || ""}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end">
      <div data-testid="followups-drawer" className="bg-white w-full max-w-xl h-full overflow-y-auto border-l border-slate-200 shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Follow-ups</div>
            <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{lead.name}</h3>
            {lead.company_name && <div className="text-xs text-slate-500">{lead.company_name}</div>}
          </div>
          <button data-testid="followups-close" onClick={onClose} className="p-2 hover:bg-slate-100"><X size={20} /></button>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Last contact</div>
              <div className="text-sm font-semibold text-slate-900 mt-1">{formatDateTime(lead.last_follow_up_at)}</div>
              {lead.last_follow_up_remarks && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{lead.last_follow_up_remarks}</div>}
            </div>
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Next due</div>
              <div className="text-sm font-semibold text-slate-900 mt-1">{formatDateTime(lead.next_follow_up_at)}</div>
              <div className="text-xs text-slate-500 mt-1">{lead.follow_up_count || 0} total logged</div>
            </div>
          </div>

          <button
            data-testid="followup-add-toggle"
            onClick={() => setShowForm(s => !s)}
            className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2.5 font-medium transition-colors inline-flex items-center justify-center gap-2 mb-5"
          >
            {showForm ? <><X size={16} /> Cancel</> : <><Plus size={16} weight="bold" /> Log Follow-up</>}
          </button>

          {showForm && (
            <form data-testid="followup-form" onSubmit={submit} className="bg-slate-50 border border-slate-200 p-4 mb-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Type</label>
                  <select data-testid="followup-type" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="rounded-none bg-white border border-slate-300 p-2 w-full outline-none text-sm">
                    {FOLLOW_UP_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Outcome</label>
                  <select
                    data-testid="followup-outcome"
                    value={form.outcome}
                    onChange={(e) => {
                      const o = e.target.value;
                      // Auto-suggest status from outcome ONLY if the user has not manually edited the status field.
                      setForm((f) => ({
                        ...f,
                        outcome: o,
                        status: f.statusManuallySet ? f.status : (OUTCOME_TO_STATUS_HINT[o] || f.status || ""),
                      }));
                    }}
                    className="rounded-none bg-white border border-slate-300 p-2 w-full outline-none text-sm">
                    {FOLLOW_UP_OUTCOMES.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Follow-up Status <span className="text-[10px] text-slate-400 ml-1 font-normal">(master · auto-suggests from outcome)</span></label>
                <FollowupStatusSelect
                  value={form.status}
                  onChange={(v) => setForm((f) => ({ ...f, status: v, statusManuallySet: true }))}
                  user={user}
                  testId="followup-status-drawer"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Remarks</label>
                <textarea data-testid="followup-remarks" value={form.remarks} onChange={(e) => setForm({...form, remarks: e.target.value})} className="rounded-none bg-white border border-slate-300 p-2 w-full outline-none text-sm" rows="3" placeholder="Discussion notes, customer response…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Next follow-up</label>
                  <input data-testid="followup-next" type="datetime-local" value={form.next_follow_up_at} onChange={(e) => setForm({...form, next_follow_up_at: e.target.value})} className="rounded-none bg-white border border-slate-300 p-2 w-full outline-none text-sm" />
                </div>
                {form.outcome === "completed" && (
                  <div>
                    <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Completed at</label>
                    <input data-testid="followup-completed-at" type="datetime-local" value={form.completed_at} onChange={(e) => setForm({...form, completed_at: e.target.value})} className="rounded-none bg-white border border-slate-300 p-2 w-full outline-none text-sm" />
                  </div>
                )}
              </div>
              <button data-testid="followup-submit" type="submit" className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 font-medium transition-colors">
                Save Follow-up
              </button>
            </form>
          )}

          {/* History */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">History</div>
            {loading ? (
              <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div data-testid="followups-empty" className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200">No follow-ups yet</div>
            ) : (
              items.map((f, i) => (
                <div key={f.follow_up_id} data-testid={`followup-item-${i}`} className={`border p-3 ${f.is_overdue ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider font-bold text-[#002FA7]">{(f.type || "").replace(/_/g, " ")}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                        f.outcome === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        f.outcome === "missed" ? "bg-rose-50 text-rose-700 border-rose-200" :
                        f.outcome === "rescheduled" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-blue-50 text-blue-700 border-blue-200"
                      }`} title="Outcome">{(f.outcome || "").replace(/_/g, " ") || "—"}</span>
                      {(() => {
                        const sm = statusMeta.find((s) => s.slug === f.status || s.name === f.status);
                        if (!f.status && !sm) return null;
                        const label = sm?.name || String(f.status || "").replace(/_/g, " ");
                        return (
                          <span
                            data-testid={`followup-item-${i}-status`}
                            title="Follow-up Status"
                            className={`${statusBadgeCls(sm?.color)} text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold`}
                          >{label}</span>
                        );
                      })()}
                      {f.is_overdue && <span className="text-[10px] text-rose-700 font-bold">OVERDUE</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      {f.outcome !== "completed" && (
                        <button data-testid={`followup-complete-${i}`} onClick={() => markComplete(f.follow_up_id)} className="p-1 text-emerald-700 hover:bg-emerald-50" title="Mark complete">
                          <CheckCircle size={16} weight="bold" />
                        </button>
                      )}
                      {["admin", "manager", "bdm"].includes(user?.role) && (
                        <button data-testid={`followup-delete-${i}`} onClick={() => remove(f.follow_up_id)} className="p-1 text-rose-700 hover:bg-rose-50" title="Delete">
                          <X size={16} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                  {f.remarks && <div className="text-sm text-slate-700 whitespace-pre-wrap">{f.remarks}</div>}
                  <div className="text-[10px] text-slate-400 mt-2 flex flex-wrap gap-3">
                    <span>By {f.by_user_name || "—"}</span>
                    <span>Logged {formatDateTime(f.created_at)}</span>
                    {f.next_follow_up_at && <span>Next {formatDateTime(f.next_follow_up_at)}</span>}
                    {f.completed_at && <span>Done {formatDateTime(f.completed_at)}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// expose helper for tests (no-op)
void toLocalInput;

export default Leads;
